import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedProject = ({ project, onViewDetails }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === project.gallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? project.gallery.length - 1 : prev - 1
    );
  };

  return (
    <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-brand-purple/5 border border-primary/20 rounded-2xl p-8 mb-12">
      {/* Featured Badge */}
      <div className="flex items-center space-x-2 mb-6">
        <div className="flex items-center space-x-2 px-3 py-1 bg-warning/10 border border-warning/20 rounded-full">
          <Icon name="Star" size={16} className="text-warning fill-current" />
          <span className="text-sm font-medium text-warning">Featured Project</span>
        </div>
        <div className="flex items-center space-x-2 px-3 py-1 bg-success/10 border border-success/20 rounded-full">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-success">{project.status}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Project Info */}
        <div className="space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-text-primary mb-3">{project.title}</h2>
            <p className="text-lg text-text-secondary leading-relaxed">{project.description}</p>
          </div>

          {/* Key Highlights */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-text-primary">Key Highlights</h3>
            <div className="space-y-2">
              {project.highlights.map((highlight, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                  <span className="text-text-secondary">{highlight}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tech Stack */}
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-3">Technology Stack</h3>
            <div className="flex flex-wrap gap-2">
              {project.techStack.slice(0, 6).map((tech, index) => (
                <span
                  key={index}
                  className="px-3 py-1 text-sm font-medium bg-primary/10 border border-primary/20 rounded-full text-primary"
                >
                  {tech}
                </span>
              ))}
              {project.techStack.length > 6 && (
                <span className="px-3 py-1 text-sm font-medium bg-accent/10 border border-accent/20 rounded-full text-accent">
                  +{project.techStack.length - 6} more
                </span>
              )}
            </div>
          </div>

          {/* Metrics */}
          {project.metrics && (
            <div className="grid grid-cols-2 gap-4">
              {project.metrics.map((metric, index) => (
                <div key={index} className="text-center p-4 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20">
                  <div className="text-2xl font-bold text-primary mb-1">{metric.value}</div>
                  <div className="text-sm text-text-muted">{metric.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="default"
              iconName="Eye"
              iconPosition="left"
              onClick={() => onViewDetails(project)}
            >
              View Case Study
            </Button>
            
            {project.liveUrl && (
              <Button
                variant="outline"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={() => window.open(project.liveUrl, '_blank')}
              >
                Live Demo
              </Button>
            )}
            
            {project.githubUrl && (
              <Button
                variant="ghost"
                iconName="Github"
                iconPosition="left"
                onClick={() => window.open(project.githubUrl, '_blank')}
                className="text-text-secondary hover:text-text-primary"
              >
                Source Code
              </Button>
            )}
          </div>
        </div>

        {/* Project Gallery */}
        <div className="relative">
          <div className="relative h-80 rounded-2xl overflow-hidden bg-muted shadow-xl">
            <Image
              src={project.gallery[currentImageIndex]}
              alt={`${project.title} screenshot ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation Arrows */}
            {project.gallery.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-premium"
                >
                  <Icon name="ChevronLeft" size={20} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-premium"
                >
                  <Icon name="ChevronRight" size={20} />
                </button>
              </>
            )}

            {/* Image Indicators */}
            {project.gallery.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {project.gallery.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-premium ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Floating Stats */}
          <div className="absolute -bottom-6 -right-6 bg-white border border-border rounded-xl p-4 shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="TrendingUp" size={20} className="text-primary" />
              </div>
              <div>
                <div className="text-lg font-bold text-text-primary">{project.year}</div>
                <div className="text-xs text-text-muted">Completed</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedProject;